import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 28  -  Trigger Logic & DAQ
// ============================================

/** 28.1 – Why We Need Triggers */
export const TriggerNeed = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Why We Need Triggers", W / 2, 24);

    // The problem
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 65, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 65, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("The Data Rate Problem", W / 2, 66);
    ctx.fillStyle = "#EF4444"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("LHC: 40 MHz collisions �- ~1 MB/event = 40 TB/s raw data", W / 2, 85);
    ctx.fillText("Storage capacity: ~1 GB/s → must reject 99.9975% of events!", W / 2, 100);

    // Funnel diagram
    const levels = [
      { label: "40 MHz", desc: "Bunch crossings", w: 370, color: "#EF4444" },
      { label: "100 kHz", desc: "After L1 trigger (hardware)", w: 280, color: "#F59E0B" },
      { label: "~1 kHz", desc: "After HLT (software)", w: 190, color: "#10B981" },
      { label: "~100 Hz", desc: "Stored to tape", w: 120, color: "#3B82F6" },
    ];
    levels.forEach((lv, i) => {
      const y = 125 + i * 50, x = W / 2 - lv.w / 2;
      ctx.fillStyle = lv.color + "15";
      ctx.beginPath(); ctx.roundRect(x, y, lv.w, 38, 6); ctx.fill();
      ctx.strokeStyle = lv.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, lv.w, 38, 6); ctx.stroke();
      ctx.fillStyle = lv.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(lv.label, W / 2 - 60, y + 24);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(lv.desc, W / 2 + 50, y + 24);
    });
    // Connecting lines
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
      const y1 = 163 + i * 50, y2 = 125 + (i + 1) * 50;
      ctx.beginPath(); ctx.moveTo(W / 2, y1); ctx.lineTo(W / 2, y2); ctx.stroke();
    }

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Trigger: real-time decision to keep or discard each event", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 28.2 – Level-1 Hardware Trigger */
export const L1HardwareTrigger = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Level-1 Hardware Trigger", W / 2, 24);

    // Key constraints
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Decision in ~2.5 μs  -  custom electronics (FPGAs, ASICs)", W / 2, 63);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Reduces 40 MHz → ~100 kHz  -  pipeline latency of 128 bunch crossings", W / 2, 82);

    // L1 subsystems
    const subs = [
      { name: "Calorimeter\nTrigger", desc: "High-E_T jets, electrons,\nphotons, MET", color: "#EF4444", x: 30 },
      { name: "Muon\nTrigger", desc: "High-p_T muon tracks\nfrom muon chambers", color: "#8B5CF6", x: 200 },
      { name: "Global\nTrigger", desc: "Combines all L1 results\nOR of trigger menus", color: "#10B981", x: 380 },
    ];
    subs.forEach((s) => {
      const y = 105, w = 155, h = 90;
      ctx.fillStyle = s.color + "12";
      ctx.beginPath(); ctx.roundRect(s.x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(s.x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 12px Inter,sans-serif";
      s.name.split("\n").forEach((ln, i) => ctx.fillText(ln, s.x + w / 2, y + 20 + i * 14));
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      s.desc.split("\n").forEach((ln, i) => ctx.fillText(ln, s.x + w / 2, y + 52 + i * 13));
    });
    // Arrows to Global
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(185, 150); ctx.lineTo(380, 150); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(355, 150); ctx.lineTo(380, 150); ctx.stroke();

    // BL4S trigger
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 210, 500, 65, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 210, 500, 65, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("BL4S Trigger (simpler!)", W / 2, 230);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Scintillator coincidence: S1 AND S2 within time window", W / 2, 250);
    ctx.fillText("NIM/CAMAC logic modules  -  ns timing, hardware coincidence unit", W / 2, 268);

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("At LHC: L1 uses coarse-granularity calorimeter + muon chamber data only", W / 2, 300);
    ctx.fillText("Full tracking too slow for L1 at 40 MHz (used from Run 3 onwards)", W / 2, 318);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 28.3 – High-Level Trigger (Software) */
export const HLTSoftwareTrigger = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("High-Level Trigger (HLT)  -  Software Trigger", W / 2, 24);

    // Architecture
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 60, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 60, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Farm of ~40,000 CPU cores running reconstruction algorithms", W / 2, 65);
    ctx.fillStyle = "#8B5CF6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Full detector readout for L1-accepted events  -  decision in ~300 ms", W / 2, 85);

    // Processing steps
    const steps = [
      { step: "1", title: "Region of Interest (RoI)", desc: "Focus on detector regions flagged by L1", color: "#3B82F6" },
      { step: "2", title: "Fast reconstruction", desc: "Track finding + vertex in RoI (fast algorithms)", color: "#8B5CF6" },
      { step: "3", title: "Full reconstruction", desc: "Complete event build if fast stage passes", color: "#10B981" },
      { step: "4", title: "Selection", desc: "Apply physics-based cuts (mass, pT, isolation)", color: "#F59E0B" },
      { step: "5", title: "Categorize", desc: "Tag into streams: electron, muon, jet, photon", color: "#EF4444" },
    ];
    steps.forEach((s, i) => {
      const y = 120 + i * 32;
      ctx.fillStyle = s.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.step, 40, y);
      ctx.font = "bold 12px Inter,sans-serif"; ctx.fillText(s.title, 60, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(s.desc, 240, y);
    });
    ctx.textAlign = "center";

    // Output rates
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 285, 500, 45, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 285, 500, 45, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("HLT output: ~1 kHz events written to storage (CERN Tier-0)", W / 2, 305);
    ctx.fillStyle = "#10B981"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Each event ~1 MB → sustainable ~1 GB/s write rate", W / 2, 322);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 28.4 – Coincidence Logic and NIM Modules */
export const CoincidenceNIM = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Coincidence Logic & NIM Modules", W / 2, 24);

    // NIM standard
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 240, 100, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 240, 100, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("NIM Standard", 150, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Logic 1: −0.8 V (−16 mA)", 150, 85);
    ctx.fillText("Logic 0: 0 V (0 mA)", 150, 100);
    ctx.fillText("Fast: ~ns edges", 150, 115);
    ctx.fillText("50 Ω terminated", 150, 130);

    // Coincidence logic diagram
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 100, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 100, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("AND Coincidence", 415, 65);
    // Two inputs
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(310, 90); ctx.lineTo(370, 90); ctx.stroke();
    ctx.fillStyle = "#10B981"; ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left"; ctx.fillText("S1 (scint.)", 310, 84);
    ctx.strokeStyle = "#3B82F6";
    ctx.beginPath(); ctx.moveTo(310, 115); ctx.lineTo(370, 115); ctx.stroke();
    ctx.fillStyle = "#3B82F6"; ctx.fillText("S2 (scint.)", 310, 109);
    // AND gate
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath(); ctx.roundRect(375, 80, 50, 46, 5); ctx.fill();
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 14px Inter,sans-serif";
    ctx.textAlign = "center"; ctx.fillText("AND", 400, 108);
    // Output
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(425, 103); ctx.lineTo(520, 103); ctx.stroke();
    ctx.fillStyle = "#EF4444"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Trigger!", 480, 97);

    // Common NIM modules
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Common NIM Modules at BL4S", W / 2, 170);
    const modules = [
      { name: "Discriminator", desc: "Analog signal → digital NIM pulse when above threshold", color: "#3B82F6" },
      { name: "Coincidence Unit", desc: "AND/OR of inputs  -  requires signals within gate width", color: "#8B5CF6" },
      { name: "Gate Generator", desc: "Produce fixed-width gate from trigger edge", color: "#10B981" },
      { name: "Scaler", desc: "Count number of triggers (rates, normalization)", color: "#F59E0B" },
      { name: "Delay", desc: "Cable or electronic delay to align signal timing", color: "#EF4444" },
      { name: "Fan-In/Fan-Out", desc: "Split one signal into many, or combine many into one", color: "#64748B" },
    ];
    modules.forEach((m, i) => {
      const y = 190 + i * 22;
      ctx.fillStyle = m.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(m.name, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(m.desc, 180, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("BL4S uses NIM/CAMAC crates  -  you design the trigger logic yourself!", W / 2, 330);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 28.5 – Data Acquisition (DAQ) Architecture */
export const DAQArchitecture = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Data Acquisition (DAQ) Architecture", W / 2, 24);

    // Pipeline flow
    const pipeline = [
      { label: "Detectors", desc: "Analog signals\nfrom sensors", color: "#EF4444", w: 90 },
      { label: "Front-end\nelectronics", desc: "Amplify, shape,\ndigitize (ADC)", color: "#F59E0B", w: 100 },
      { label: "Readout\nbuffers", desc: "Store during\ntrigger latency", color: "#3B82F6", w: 100 },
      { label: "Event\nbuilder", desc: "Assemble full\nevent fragments", color: "#8B5CF6", w: 100 },
      { label: "Storage", desc: "Write to disk\n/ tape", color: "#10B981", w: 80 },
    ];
    let xPos = 15;
    pipeline.forEach((p, i) => {
      const y = 50, h = 80;
      ctx.fillStyle = p.color + "15";
      ctx.beginPath(); ctx.roundRect(xPos, y, p.w, h, 8); ctx.fill();
      ctx.strokeStyle = p.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(xPos, y, p.w, h, 8); ctx.stroke();
      ctx.fillStyle = p.color; ctx.font = "bold 9px Inter,sans-serif";
      p.label.split("\n").forEach((ln, li) => ctx.fillText(ln, xPos + p.w / 2, y + 16 + li * 12));
      ctx.fillStyle = "#475569"; ctx.font = "9px Inter,sans-serif";
      p.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, xPos + p.w / 2, y + 48 + li * 11));
      if (i < pipeline.length - 1) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(xPos + p.w, y + h / 2); ctx.lineTo(xPos + p.w + 12, y + h / 2); ctx.stroke();
        ctx.fillStyle = "#94A3B8";
        ctx.beginPath(); ctx.moveTo(xPos + p.w + 12, y + h / 2); ctx.lineTo(xPos + p.w + 8, y + h / 2 - 3); ctx.lineTo(xPos + p.w + 8, y + h / 2 + 3); ctx.closePath(); ctx.fill();
      }
      xPos += p.w + 14;
    });

    // Key concepts
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("DAQ Key Concepts", W / 2, 155);
    const concepts = [
      { concept: "Dead time", desc: "Period when DAQ cannot accept new triggers (minimize!)", color: "#EF4444" },
      { concept: "Throughput", desc: "Max data rate: limited by slowest link in chain", color: "#F59E0B" },
      { concept: "Buffer depth", desc: "How many events can queue during trigger decision", color: "#3B82F6" },
      { concept: "Event building", desc: "Merge fragments from all sub-detectors into one event", color: "#8B5CF6" },
      { concept: "Clock distribution", desc: "40 MHz LHC clock synchronized across entire experiment", color: "#10B981" },
    ];
    concepts.forEach((co, i) => {
      const y = 175 + i * 22;
      ctx.fillStyle = co.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(co.concept, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(co.desc, 190, y);
    });
    ctx.textAlign = "center";

    // BL4S DAQ
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 290, 500, 40, 8); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(30, 290, 500, 40, 8); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("BL4S DAQ: CAMAC ADC/TDC modules → PC readout → ROOT files", W / 2, 315);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 28.6 – Trigger Menus and Prescaling */
export const TriggerMenuPrescale = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Trigger Menus & Prescaling", W / 2, 24);

    // What is a trigger menu
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 50, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 50, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Trigger Menu: list of all defined trigger paths with thresholds", W / 2, 65);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Each trigger = condition (e.g. single muon pT > 26 GeV)  -  event kept if ANY triggers fire", W / 2, 85);

    // Example menu
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Example Trigger Menu (CMS Run 2)", W / 2, 115);
    const triggers = [
      { path: "HLT_Mu50", condition: "Single muon pT > 50 GeV", prescale: "1", rate: "~10 Hz", color: "#8B5CF6" },
      { path: "HLT_Ele32", condition: "Single electron ET > 32 GeV", prescale: "1", rate: "~15 Hz", color: "#3B82F6" },
      { path: "HLT_DiJet40", condition: "Two jets ET > 40 GeV each", prescale: "100", rate: "~5 Hz", color: "#10B981" },
      { path: "HLT_Photon200", condition: "Single photon ET > 200 GeV", prescale: "1", rate: "~2 Hz", color: "#F59E0B" },
    ];
    // Header
    ctx.fillStyle = "#94A3B8"; ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Trigger Path", 40, 133); ctx.fillText("Condition", 170, 133);
    ctx.fillText("Prescale", 380, 133); ctx.fillText("Rate", 450, 133);
    triggers.forEach((t, i) => {
      const y = 150 + i * 22;
      ctx.fillStyle = t.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(t.path, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(t.condition, 170, y);
      ctx.fillStyle = t.prescale === "1" ? "#10B981" : "#EF4444"; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(t.prescale, 395, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(t.rate, 450, y);
    });
    ctx.textAlign = "center";

    // Prescaling explanation
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 240, 500, 60, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 240, 500, 60, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Prescaling", W / 2, 260);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Prescale = N → keep only 1 in every N events passing that trigger", W / 2, 278);
    ctx.fillText("Used for high-rate low-threshold triggers to control total output rate", W / 2, 293);

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Total HLT output must fit bandwidth budget (~1 kHz for CMS/ATLAS)", W / 2, 325);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 28.7 – Designing a BL4S Trigger */
export const BL4STriggerDesign = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Designing Your BL4S Trigger", W / 2, 24);

    // Trigger design steps
    const steps = [
      { step: "1", title: "Define signal", desc: "What physics event do you want?\ne.g., particle crossing both scintillators", color: "#3B82F6" },
      { step: "2", title: "Choose detectors", desc: "Which detectors must fire?\ne.g., S1 AND S2 for through-going track", color: "#8B5CF6" },
      { step: "3", title: "Set thresholds", desc: "Discriminator threshold above noise\nAvoid false triggers from PMT dark current", color: "#10B981" },
      { step: "4", title: "Timing alignment", desc: "Use delay cables/modules to align\nsignals within coincidence gate (~20 ns)", color: "#F59E0B" },
      { step: "5", title: "Test and optimize", desc: "Measure trigger rate, adjust thresholds\nCheck efficiency with known beam", color: "#EF4444" },
    ];
    steps.forEach((s, i) => {
      const y = 45 + i * 56;
      ctx.fillStyle = s.color + "10";
      ctx.beginPath(); ctx.roundRect(20, y, 520, 48, 8); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(20, y, 520, 48, 8); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.step, 32, y + 18);
      ctx.font = "bold 12px Inter,sans-serif"; ctx.fillText(s.title, 55, y + 18);
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      s.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, 55, y + 33 + li * 12));
    });
    ctx.textAlign = "center";

    // Tip
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 330, 500, 25, 6); ctx.fill();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Pro tip: measure trigger efficiency = (triggered events) / (all real events from scaler)", W / 2, 347);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

// ============================================
// Chapter 29  -  Beyond the Standard Model (BSM)
// ============================================

/** 29.1 – Why Physics Beyond the SM? */
export const WhyBSM = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Why Physics Beyond the Standard Model?", W / 2, 24);

    // SM successes
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 45, 240, 120, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 240, 120, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("SM Successes", 150, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    const succ = ["Predicted W, Z, Higgs bosons", "QED: g-2 to 10 decimal places", "Quark model explains hadrons", "Electroweak unification"];
    succ.forEach((s, i) => ctx.fillText(s, 150, 85 + i * 17));

    // SM problems
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(290, 45, 240, 120, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 240, 120, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("SM Problems", 410, 65);
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    const prob = ["No gravity (no graviton)", "No dark matter candidate", "No dark energy explanation", "Neutrino masses are zero in SM"];
    prob.forEach((p, i) => ctx.fillText(p, 410, 85 + i * 17));

    // Open questions
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Major Open Questions", W / 2, 190);
    const questions = [
      { q: "Hierarchy Problem", desc: "Why is m_H ~ 125 GeV ≪ M_Planck ~ 10¹⁹ GeV? Needs fine-tuning!", color: "#EF4444" },
      { q: "Matter-Antimatter", desc: "Why is there more matter? CP violation in SM is insufficient", color: "#F59E0B" },
      { q: "Dark Matter", desc: "27% of universe  -  no SM particle fits (WIMPs? Axions?)", color: "#8B5CF6" },
      { q: "Neutrino Masses", desc: "Oscillations prove mν ≠ 0, but SM has massless neutrinos", color: "#3B82F6" },
      { q: "Unification", desc: "Do forces merge at high energy? (GUTs, string theory)", color: "#10B981" },
      { q: "Flavour Puzzle", desc: "Why 3 generations? Why these mass ratios?", color: "#64748B" },
    ];
    questions.forEach((q, i) => {
      const y = 210 + i * 22;
      ctx.fillStyle = q.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(q.q, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(q.desc, 190, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("BSM physics: any theory extending the SM to address these puzzles", W / 2, 350);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 29.2 – Supersymmetry (SUSY) */
export const Supersymmetry = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Supersymmetry (SUSY)", W / 2, 24);

    // Core idea
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Every fermion ↔ boson partner  (spin differs by ½)", W / 2, 63);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Doubles the particle spectrum  -  partners not yet observed (broken SUSY)", W / 2, 82);

    // SM ↔ SUSY table
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("SM Particle → SUSY Partner", W / 2, 110);
    const pairs = [
      { sm: "electron (e)", susy: "selectron (ẽ)", smSpin: "½", susySpin: "0", color: "#3B82F6" },
      { sm: "quark (q)", susy: "squark (q̃)", smSpin: "½", susySpin: "0", color: "#8B5CF6" },
      { sm: "photon (γ)", susy: "photino (γ̃)", smSpin: "1", susySpin: "½", color: "#F59E0B" },
      { sm: "gluon (g)", susy: "gluino (g̃)", smSpin: "1", susySpin: "½", color: "#EF4444" },
      { sm: "W boson", susy: "wino (W̃)", smSpin: "1", susySpin: "½", color: "#10B981" },
      { sm: "Higgs (H)", susy: "higgsino (H̃)", smSpin: "0", susySpin: "½", color: "#64748B" },
    ];
    ctx.font = "bold 10px Inter,sans-serif"; ctx.fillStyle = "#94A3B8";
    ctx.textAlign = "left";
    ctx.fillText("SM Particle", 40, 128); ctx.fillText("Spin", 160, 128);
    ctx.fillText("SUSY Partner", 210, 128); ctx.fillText("Spin", 340, 128);
    pairs.forEach((p, i) => {
      const y = 145 + i * 18;
      ctx.fillStyle = p.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(p.sm, 40, y); ctx.fillText(p.smSpin, 168, y);
      ctx.fillText(p.susy, 210, y); ctx.fillText(p.susySpin, 348, y);
    });
    ctx.textAlign = "center";

    // Why SUSY?
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 260, 500, 90, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 260, 500, 90, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Why SUSY Is Attractive", W / 2, 280);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Solves hierarchy problem (cancels quadratic divergences)", W / 2, 298);
    ctx.fillText("Gauge coupling unification at ~10¹⁶ GeV", W / 2, 314);
    ctx.fillText("Lightest SUSY particle (LSP) = dark matter candidate", W / 2, 330);
    ctx.fillText("Status: no SUSY particles found at LHC (yet)  -  masses pushed > 1-2 TeV", W / 2, 346);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 29.3 – Dark Matter Searches */
export const DarkMatterSearches = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Dark Matter Searches", W / 2, 24);

    // Evidence
    ctx.fillStyle = "#F0F9FF";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 55, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 55, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Evidence: rotation curves, CMB, gravitational lensing, structure formation", W / 2, 65);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("~27% of universe energy  -  interacts gravitationally, not electromagnetically", W / 2, 85);

    // Three search strategies
    const strategies = [
      { name: "Direct Detection", desc: "DM particle scatters off nucleus\nin underground detector\n(XENON, LZ, PandaX)", arrow: "DM → nucleus", color: "#EF4444" },
      { name: "Indirect Detection", desc: "DM annihilates → SM particles\nObserve γ-rays, neutrinos etc.\n(Fermi-LAT, IceCube)", arrow: "DM DM → SM SM", color: "#8B5CF6" },
      { name: "Collider Production", desc: "Produce DM at LHC from SM\ncollisions → missing ET\n(ATLAS, CMS mono-X)", arrow: "SM SM → DM DM", color: "#10B981" },
    ];
    strategies.forEach((s, i) => {
      const x = 20 + i * 180, y = 115, w = 170, h = 120;
      ctx.fillStyle = s.color + "10";
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(s.name, x + w / 2, y + 18);
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      s.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 36 + li * 13));
      ctx.fillStyle = s.color; ctx.font = "bold 10px Inter,sans-serif";
      ctx.fillText(s.arrow, x + w / 2, y + h - 8);
    });

    // Candidates
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("DM Candidates", W / 2, 255);
    const cands = [
      { name: "WIMP", mass: "10 GeV – 10 TeV", desc: "Weakly interacting  -  naturally gives right abundance", color: "#EF4444" },
      { name: "Axion", mass: "10⁻⁶ – 10⁻³ eV", desc: "Solves strong CP problem too (ADMX, ABRACADABRA)", color: "#3B82F6" },
      { name: "Sterile ν", mass: "keV range", desc: "Heavy neutrino, warm dark matter candidate", color: "#8B5CF6" },
    ];
    cands.forEach((cd, i) => {
      const y = 272 + i * 22;
      ctx.fillStyle = cd.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(cd.name, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(cd.mass, 130, y);
      ctx.fillText(cd.desc, 270, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Dark matter is one of the biggest open questions in physics today", W / 2, 350);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 29.4 – Neutrino Masses and Oscillations */
export const NeutrinoMassOscillation = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Neutrino Masses & Oscillations", W / 2, 24);

    // Key discovery
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 50, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 50, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Neutrinos change flavour while propagating → they must have mass!", W / 2, 65);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Nobel Prize 2015: Kajita (Super-K) + McDonald (SNO)", W / 2, 82);

    // Oscillation formula
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(30, 110, 500, 45, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 110, 500, 45, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("P(νμ → νe) = sin²(2θ) · sin²(1.27 Δm² L/E)", W / 2, 130);
    ctx.fillStyle = "#8B5CF6"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("θ = mixing angle, Δm² [eV²], L [km], E [GeV]", W / 2, 148);

    // Three flavours mixing
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Three-Flavour Mixing (PMNS Matrix)", W / 2, 175);
    const flavours = [
      { flav: "νₑ", mass: "ν₁, ν₂, ν₃", exp: "Solar + reactor", color: "#3B82F6" },
      { flav: "νμ", mass: "ν₁, ν₂, ν₃", exp: "Atmospheric + accelerator", color: "#8B5CF6" },
      { flav: "ντ", mass: "ν₁, ν₂, ν₃", exp: "OPERA (τ appearance)", color: "#10B981" },
    ];
    flavours.forEach((f, i) => {
      const y = 195 + i * 23;
      ctx.fillStyle = f.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(f.flav, 60, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(`= superposition of ${f.mass}`, 100, y);
      ctx.fillText(f.exp, 330, y);
    });
    ctx.textAlign = "center";

    // Open questions
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 275, 500, 75, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 275, 500, 75, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Open Questions", W / 2, 295);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Mass ordering: Normal (m₁ < m₂ < m₃) or Inverted (m₃ < m₁ < m₂)?", W / 2, 313);
    ctx.fillText("CP violation in leptons: δ_CP ≠ 0?  (DUNE, Hyper-K will measure)", W / 2, 330);
    ctx.fillText("Dirac or Majorana? If Majorana → neutrinoless double-beta decay possible", W / 2, 347);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 29.5 – Extra Dimensions and Exotic Theories */
export const ExtraDimensions = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Extra Dimensions & Exotic Theories", W / 2, 24);

    // Large Extra Dimensions (ADD)
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(20, 45, 250, 110, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 45, 250, 110, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("ADD: Large Extra Dims", 145, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Gravity leaks into n extra dims", 145, 85);
    ctx.fillText("Explains weakness of gravity", 145, 100);
    ctx.fillText("LHC signature: missing ET", 145, 115);
    ctx.fillText("(graviton escapes to bulk)", 145, 130);
    ctx.fillText("M_D could be ~ TeV", 145, 145);

    // Warped Extra Dims (RS)
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 110, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 110, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("RS: Warped Extra Dim", 415, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("1 extra dim with warp factor", 415, 85);
    ctx.fillText("Two branes: Planck + TeV", 415, 100);
    ctx.fillText("Hierachy from exponential", 415, 115);
    ctx.fillText("warp: e^(−kπr) suppression", 415, 130);
    ctx.fillText("Signal: KK graviton resonance", 415, 145);

    // Other exotic models
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Other Exotic BSM Theories", W / 2, 180);
    const exotics = [
      { name: "Technicolor", desc: "New strong force breaks EW symmetry (no fundamental Higgs)", color: "#EF4444" },
      { name: "Composite Higgs", desc: "Higgs is bound state of new strong dynamics", color: "#F59E0B" },
      { name: "Leptoquarks", desc: "Particles coupling leptons to quarks  -  explain B-anomalies?", color: "#10B981" },
      { name: "Magnetic monopoles", desc: "Dirac prediction: magnetic charge  -  never found", color: "#3B82F6" },
      { name: "String theory", desc: "Fundamental strings at ~10⁻³⁵ m  -  not directly testable (yet)", color: "#8B5CF6" },
    ];
    exotics.forEach((e, i) => {
      const y = 198 + i * 22;
      ctx.fillStyle = e.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(e.name, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(e.desc, 190, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("LHC searches have tested many BSM models  -  so far SM prevails!", W / 2, 330);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 29.6 – Anomalies and Hints of New Physics */
export const BSMAnomalies = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Anomalies & Hints of New Physics", W / 2, 24);

    const anomalies = [
      { name: "Muon g-2", status: "Active", desc: "Muon magnetic moment: experiment vs SM prediction\naμ(exp) − aμ(SM) ~ 4.2σ discrepancy (Fermilab 2021)\nLattice QCD may reduce tension  -  situation evolving", color: "#EF4444", sigma: "~4.2σ" },
      { name: "B-meson anomalies", status: "Fading", desc: "Lepton universality: R(K), R(K*) deviated from 1\nLHCb 2022 update: consistent with SM after reanalysis\nWas ~3σ, now ~1σ", color: "#F59E0B", sigma: "~1σ" },
      { name: "W boson mass", status: "Disputed", desc: "CDF II (2022): mW = 80.4335 GeV (7σ from SM!)\nBut ATLAS (2024): mW = 80.3665 GeV (agrees with SM)\nMay be systematic issue at CDF", color: "#8B5CF6", sigma: "Unclear" },
      { name: "Cabibbo anomaly", status: "Active", desc: "Unitarity of first row of CKM matrix: |Vud|²+|Vus|²+|Vub|² < 1?\nCould indicate new particles or radiative corrections", color: "#10B981", sigma: "~3σ" },
    ];
    anomalies.forEach((a, i) => {
      const y = 45 + i * 77;
      ctx.fillStyle = a.color + "08";
      ctx.beginPath(); ctx.roundRect(20, y, 520, 68, 8); ctx.fill();
      ctx.strokeStyle = a.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(20, y, 520, 68, 8); ctx.stroke();
      ctx.fillStyle = a.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(a.name, 32, y + 16);
      ctx.fillStyle = "#64748B"; ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(`[${a.status}] ${a.sigma}`, 530, y + 16);
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "left";
      a.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, 32, y + 32 + li * 12));
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Anomalies need 5σ to be confirmed  -  patience and independent replication required!", W / 2, 355);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 29.7 – BSM Searches at CERN and the Future */
export const BSMFutureSearches = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("BSM Searches: Current and Future", W / 2, 24);

    // Current searches at LHC
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 80, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 80, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("LHC Run 3 (2022–2026): √s = 13.6 TeV", W / 2, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("SUSY searches: squarks/gluinos > 2 TeV, electroweakinos > 1 TeV", W / 2, 83);
    ctx.fillText("Exotic searches: dijet resonances, mono-X (dark matter), long-lived particles", W / 2, 98);
    ctx.fillText("Precision Higgs: coupling measurements, rare decays (H→μμ)", W / 2, 113);

    // Future facilities
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Future Collider Roadmap", W / 2, 145);
    const future = [
      { name: "HL-LHC", when: "2029–2041", desc: "3000 fb⁻¹, 10�- LHC stats for precision & rare", color: "#3B82F6" },
      { name: "FCC-ee", when: "~2040s", desc: "e⁺e⁻ Higgs factory, 91 km tunnel at CERN", color: "#8B5CF6" },
      { name: "FCC-hh", when: "~2060s", desc: "100 TeV pp  -  direct discovery reach to ~40 TeV", color: "#EF4444" },
      { name: "CLIC", when: "~2040s", desc: "Multi-TeV e⁺e⁻ linear collider (alternative to FCC-ee)", color: "#10B981" },
      { name: "Muon Collider", when: "R&D", desc: "μ⁺μ⁻ at multi-TeV: clean + high energy (ambitious!)", color: "#F59E0B" },
    ];
    future.forEach((f, i) => {
      const y = 165 + i * 25;
      ctx.fillStyle = f.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(f.name, 40, y);
      ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
      ctx.fillText(f.when, 140, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(f.desc, 210, y);
    });
    ctx.textAlign = "center";

    // Message
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 300, 500, 50, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 300, 500, 50, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("The next generation of experiments will explore uncharted territory", W / 2, 320);
    ctx.fillStyle = "#10B981"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("BL4S teams contribute to this mission  -  every measurement matters!", W / 2, 340);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};
